import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import firebase from '../../firebase';
import { addSubscriberToKlaviyo } from '../../utils/klaviyo';
import { buildSignupAbFields, recordAbCohortAssignment, loadAbTestConfig } from '../../utils/abCohort';
import { signInWithGoogle } from '../../utils/googleAuth';
import GlassButton from '../Aero/GlassButton';
import AeroAuthShell, {
  AeroAuthHeading,
  AeroAuthSwitch,
  AeroGoogleButton,
  AeroAuthDivider,
  AeroAuthField,
  AeroAuthError,
  AeroPasswordInput,
} from './AeroAuthShell';

/**
 * AeroAuth — inline login/signup page rendered by AeroLayout for unauthenticated users.
 *
 * NOT a route. Rendered in-place when user === null on the Aero domain.
 * After successful auth, AeroLayout's useAuth() listener fires and the
 * dashboard renders automatically — no navigation needed.
 *
 * v3 look (25 Oct 2026): the same AeroAuthShell card as /login, so every
 * Aero sign-in looks the same.
 */
export default function AeroAuth() {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isPluginRef = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('ref') === 'figma-plugin';

  const heading = isPluginRef
    ? 'Aero is installed'
    : (mode === 'signin' ? 'Sign in to Aero' : 'Create your Aero account');
  const subheading = isPluginRef
    ? 'Create a free account to start testing.'
    : (mode === 'signin'
      ? 'Your sites and tests are where you left them.'
      : 'Free to start. Publish a prototype and watch people use it.');

  const switchMode = (next) => {
    setMode(next);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // A/B foundation (invisible): record origin + cohort via the shared
        // helper so this path assigns identically to every other signup path.
        // Gated by the admin on/off config (default OFF -> no cohort assigned).
        const abConfig = await loadAbTestConfig();
        const abFields = buildSignupAbFields(undefined, abConfig);

        const db = firebase.firestore();
        await db.collection('user').doc(user.uid).set({
          name: name,
          email: user.email,
          signUpDate: new Date().toISOString(),
          trialUsed: false,
          subscriptionStatus: 'trial',
          signUpMethod: 'password',
          ...abFields,
        });

        // Best-effort cohort counter; never throws, so a failed stats write
        // cannot break account creation.
        recordAbCohortAssignment(abFields);

        await addSubscriberToKlaviyo(email, name);
      }
    } catch (err) {
      setIsProcessing(false);
      const errorCode = err.code || '';
      const msg = err.message || '';
      if (errorCode === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (errorCode === 'auth/invalid-credential') {
        setError('Sign-in failed. If you previously used Google to sign in, use the Google button. Otherwise check your password or reset it.');
      } else if (msg.includes('user-not-found')) {
        setError('No account found with this email. Try signing up instead.');
      } else if (msg.includes('email-already-in-use')) {
        setError('Email already registered. Try signing in instead.');
      } else if (msg.includes('weak-password')) {
        setError('Password should be at least 6 characters.');
      } else if (msg.includes('too-many-requests')) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(msg);
      }
    }
  };

  // One Google button for both modes. The helper runs signup bookkeeping only
  // for a brand-new user (no entryPoint here, so no cohort, matching the email
  // signup on this surface). On success there is nothing to do: AeroLayout's
  // useAuth() listener fires and the dashboard renders in place, same as the
  // email path.
  const handleGoogleAuth = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setIsProcessing(false);
      if (!err.cancelled) {
        setError(err.message);
      }
    }
  };

  const isValid = mode === 'signin'
    ? email.trim() && password
    : email.trim() && password && name.trim();

  return (
    <AeroAuthShell
      below={(
        <p className="aero-signin-fine">
          By continuing, you agree to the{' '}
          <a href="https://app.aeroapp.ai/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="https://app.aeroapp.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>
      )}
    >
      <AeroAuthHeading title={heading}>{subheading}</AeroAuthHeading>

      <AeroAuthSwitch
        value={mode}
        onChange={switchMode}
        options={[
          { value: 'signin', label: 'Sign in' },
          { value: 'signup', label: 'Sign up' },
        ]}
      />

      <AeroGoogleButton onClick={handleGoogleAuth} disabled={isProcessing} />
      <AeroAuthDivider />

      <form className="aero-signin-form" onSubmit={handleSubmit}>
        {mode === 'signup' && (
          <AeroAuthField id="aero-auth-name" label="Name">
            <input
              id="aero-auth-name"
              className="aero-signin-input"
              type="text"
              autoComplete="name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </AeroAuthField>
        )}

        <AeroAuthField id="aero-auth-email" label="Email">
          <input
            id="aero-auth-email"
            className="aero-signin-input"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </AeroAuthField>

        <AeroAuthField
          id="aero-auth-password"
          label="Password"
          aside={mode === 'signin'
            ? <Link className="aero-signin-link" to="/forgot-password">Forgot password?</Link>
            : null}
        >
          <AeroPasswordInput
            id="aero-auth-password"
            placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            shown={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
          />
        </AeroAuthField>

        <AeroAuthError>{error}</AeroAuthError>

        <GlassButton
          fill="ink"
          size="lg"
          block
          type="submit"
          loading={isProcessing}
          disabled={!isValid}
        >
          {isProcessing
            ? (mode === 'signin' ? 'Signing in' : 'Creating your account')
            : (mode === 'signin' ? 'Sign in' : 'Create account')}
        </GlassButton>
      </form>
    </AeroAuthShell>
  );
}
